import {
  useGetFormalityRequestDetail,
  useGetFormalityRequestStatus,
} from "@/queries/formality-request";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const InfoContext = createContext<{
  shouldShowInfo: boolean;
  setShouldShowInfo: (value: boolean) => void;
  formDeadline?: Date;
  setFormDeadline: (value?: Date) => void;
  fromExpedient?: boolean;
  canBeCancelled: boolean;
  setCanBeCancelled: (value: boolean) => void;
  isCancelled?: boolean;
  isCompleted?: boolean;
  stopIntervalChecks?: boolean;
  formalityRequest?: FormalityRequestType;
  isLoadingFormalityRequest?: boolean;
  currentRequestStatus?: string;
  progress?: number;
}>({
  shouldShowInfo: false,
  setShouldShowInfo: () => {},
  formDeadline: undefined,
  setFormDeadline: () => {},
  canBeCancelled: false,
  setCanBeCancelled: () => {},
  isCompleted: false,
  isCancelled: false,
  stopIntervalChecks: false,
  formalityRequest: undefined,
  isLoadingFormalityRequest: false,
});

export const InfoProvider = ({
  procedureId,
  children,
}: {
  procedureId: string | undefined;
  children: ReactNode;
}) => {
  const [shouldShowInfo, setShouldShowInfo] = useState(true);
  const [formDeadline, setFormDeadline] = useState<Date>();
  const [canBeCancelled, setCanBeCancelled] = useState(false);
  const [stopStatusCheck, setStopStatusCheck] = useState(false);

  const { data: formalityRequestStatus } = useGetFormalityRequestStatus(
    procedureId ?? "",
    !!procedureId,
    stopStatusCheck ? 0 : 5000,
  );

  const { data: formalityRequest, isPending } = useGetFormalityRequestDetail(
    procedureId ?? "",
    stopStatusCheck,
  );

  const currentRequestStatus = useMemo(() => {
    return (
      formalityRequestStatus?.status?.toUpperCase() ||
      formalityRequest?.status?.toUpperCase() ||
      ""
    );
  }, [formalityRequestStatus?.status, formalityRequest?.status]);

  const isCancelledByRequest = useMemo(() => {
    const cancellationRequests = formalityRequest?.cancellation_requests;

    if (!cancellationRequests?.length) {
      return false;
    }

    const latestCancellation =
      cancellationRequests[cancellationRequests.length - 1];

    return Boolean(latestCancellation?.approved_at);
  }, [formalityRequest?.cancellation_requests]);

  const isCompleted = useMemo(() => {
    // Finished -> El flujo llegó a su fin.
    // Failed -> El flujo terminó con un error.
    return ["FINISHED", "FAILED"].includes(currentRequestStatus);
  }, [currentRequestStatus]);

  const isCancelled = useMemo(() => {
    return ["CANCELLED", "CANCELED"].includes(currentRequestStatus);
  }, [currentRequestStatus]);

  useEffect(() => {
    // check that the data was fetched at least once before stopping the status check
    if (
      (isCompleted || isCancelled || isCancelledByRequest) &&
      formalityRequest
    ) {
      setStopStatusCheck(true);
    }
  }, [isCompleted, isCancelled, formalityRequest]);

  const value = useMemo(
    () => ({
      shouldShowInfo,
      setShouldShowInfo,
      formDeadline,
      setFormDeadline,
      canBeCancelled,
      setCanBeCancelled,
      isCancelled,
      isCompleted,
      formalityRequest,
      stopIntervalChecks: stopStatusCheck,
      isLoadingFormalityRequest: isPending,
      progress: formalityRequestStatus?.progress,
      currentRequestStatus,
    }),
    [
      shouldShowInfo,
      setShouldShowInfo,
      formDeadline,
      setFormDeadline,
      canBeCancelled,
      setCanBeCancelled,
      isCancelled,
      isCompleted,
      formalityRequest,
      stopStatusCheck,
      isPending,
      formalityRequestStatus?.progress,
      currentRequestStatus,
    ],
  );

  return <InfoContext.Provider value={value}>{children}</InfoContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useInfo = () => {
  const context = useContext(InfoContext);
  if (context === undefined) {
    throw new Error("useInfo must be used within an InfoProvider");
  }
  return context;
};
